import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Code2, ShoppingCart, HeartPulse, ChevronRight } from "lucide-react";

const services = [
  {
    icon: Code2,
    title: "Software & App-Entwicklung",
    desc: "Massgeschneiderte Softwarelösungen für Ihre geschäftlichen Anforderungen. Webapplikationen, API-Integrationen und Prozessautomatisierung.",
    color: "#FF3B30",
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce Lösungen",
    desc: "Erfolgreiche Online-Shops und digitale Verkaufsplattformen. Von der Entwicklung bis zur Zahlungsintegration.",
    color: "#0EA5E9",
  },
  {
    icon: HeartPulse,
    title: "Healthcare Digital",
    desc: "Digitale Transformation im Gesundheitswesen. Sichere Anwendungen, Workflow-Systeme und Datenmanagement.",
    color: "#F59E0B",
  },
];

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1753006989726-6774bc201f88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzd2lzcyUyMGFscHMlMjBtb3VudGFpbnMlMjBhYnN0cmFjdCUyMGxhbmRzY2FwZSUyMGZvZ3xlbnwwfHx8fDE3NzI3MTE2Mjl8MA&ixlib=rb-4.1.0&q=85)`,
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-2xl">
            <div className="animate-fade-in-up opacity-0 stagger-1">
              <span className="inline-block text-sm font-semibold tracking-wide uppercase text-[#FF3B30] mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Digitale Lösungen aus der Schweiz
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-6 animate-fade-in-up opacity-0 stagger-2"
              style={{ fontFamily: 'Plus Jakarta Sans' }}
              data-testid="hero-title"
            >
              Digitale Lösungen für Unternehmen, E-Commerce und Gesundheitswesen.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-lg animate-fade-in-up opacity-0 stagger-3">
              ServicePoint Huwiler bietet moderne digitale Dienstleistungen für Unternehmen und Organisationen. Effizient, skalierbar und zuverlässig.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-4">
              <Link
                to="/dienstleistungen"
                className="inline-flex items-center gap-2 bg-[#FF3B30] text-white h-12 px-8 rounded-sm font-medium transition-all duration-300 hover:bg-[#E5352B] shadow-sm hover:shadow-lg"
                data-testid="hero-cta-services"
              >
                Unsere Dienstleistungen
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 border-2 border-white/30 text-white h-12 px-8 rounded-sm font-medium transition-all duration-300 hover:bg-white/10"
                data-testid="hero-cta-contact"
              >
                Kontakt aufnehmen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="text-sm font-semibold tracking-wide uppercase text-[#FF3B30]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Dienstleistungen
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0F172A] mt-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Was wir bieten
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, i) => (
              <div
                key={service.title}
                className="service-card bg-white border border-slate-100 rounded-sm p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`service-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-sm flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${service.color}10` }}
                >
                  <service.icon size={24} style={{ color: service.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  {service.desc}
                </p>
                <Link
                  to="/dienstleistungen"
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#FF3B30] group-hover:gap-2 transition-all duration-200"
                  data-testid={`service-link-${i}`}
                >
                  Mehr erfahren <ChevronRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-[#0F172A] rounded-sm p-12 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Bereit für die digitale Zukunft?
              </h2>
              <p className="text-slate-400 text-base max-w-md">
                Kontaktieren Sie uns und erfahren Sie, wie wir Ihr Unternehmen unterstützen können.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-[#FF3B30] text-white h-12 px-8 rounded-sm font-medium transition-all duration-300 hover:bg-[#E5352B] shadow-sm hover:shadow-lg shrink-0"
              data-testid="cta-contact-button"
            >
              Jetzt kontaktieren
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
